import { faBars, faLinkSimple } from '@fortawesome/pro-regular-svg-icons';
import {
  AccessibleIcon,
  Flex,
  Icon,
  IconButton,
  Text,
  Theme,
  Tooltip,
  Link,
} from '@scale/scaleui-radix';
import { useTheme } from 'next-themes';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import * as React from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import scaleUIRadixPackageJson from '@scale/scaleui-radix/package.json';
import { classNames } from '@utils/classNames';
import { NoSsr } from '@components/NoSsr';
import { BoxLink } from './BoxLink';
import styles from './Header.module.css';
import { useMobileMenuContext } from './MobileMenu';
import { ScaleUIRadixLogo } from './RadixLogo';
import { ThemeToggle } from './ThemeToggle';

export type HeaderProps = {
  children?: React.ReactNode;
  gitHubLink?: string;
  ghost?: boolean;
};

type ScrollState = 'at-top' | 'scrolling-up' | 'scrolling-down';

export function Header({ children, gitHubLink, ghost }: HeaderProps) {
  const mobileMenu = useMobileMenuContext();
  const router = useRouter();
  const { theme } = useTheme();

  const [scrollState, setScrollState] = React.useState<ScrollState>('at-top');

  React.useEffect(() => {
    let previousScrollY = window.scrollY;

    const handleScroll = () => {
      const direction = previousScrollY < window.scrollY ? 'scrolling-down' : 'scrolling-up';
      const state = window.scrollY < 30 ? 'at-top' : direction;
      previousScrollY = window.scrollY;
      setScrollState(state);
    };

    if (ghost) {
      addEventListener('scroll', handleScroll, { passive: true });
    } else {
      removeEventListener('scroll', handleScroll);
    }

    handleScroll();
    return () => {
      removeEventListener('scroll', handleScroll);
    };
  }, [ghost]);

  return (
    <Theme asChild>
      <div
        className={classNames(styles.HeaderRoot, ghost ? styles.ghost : '')}
        data-mobile-menu-open={mobileMenu.open}
        data-scroll-state={scrollState}
      >
        <div className={styles.HeaderInner}>
          {/* Components that hide the scrollbar (like Dialog) add padding to
          account for the scrollbar gap to avoid layout jank. This does not
          work for position: fixed elements. Since we use react-remove-scroll
          under the hood for those primitives, we can add this helper class
          provided by that lib to deal with that for the Header.
          https://github.com/radix-ui/website/issues/64
          https://github.com/theKashey/react-remove-scroll#positionfixed-elements */}
          <div
            className={RemoveScroll.classNames.fullWidth}
            style={{
              position: 'absolute',
              height: 'inherit',
              top: 0,
              left: 0,
              right: 0,
            }}
          >
            <Flex
              align="center"
              bottom="0"
              display={{ sm: 'none' }}
              left="0"
              pl="4"
              position="absolute"
              top="0"
            >
              {mobileMenu.open ? (
                <NextLink href="/" legacyBehavior passHref>
                  <BoxLink>
                    <AccessibleIcon label="Radix Homepage">
                      <Icon icon={faLinkSimple} label="Radix Homepage" />
                    </AccessibleIcon>
                  </BoxLink>
                </NextLink>
              ) : (
                <RadixByWorkOSLogoLink />
              )}
            </Flex>
            <Flex
              align="center"
              bottom="0"
              display={{ initial: 'none', sm: 'flex' }}
              left="0"
              pl="4"
              position="absolute"
              top="0"
            >
              <NextLink href="/" legacyBehavior passHref>
                <BoxLink>
                  <AccessibleIcon label="Scale UI Radix">
                    <NoSsr>
                      <ScaleUIRadixLogo theme={theme} />
                    </NoSsr>
                  </AccessibleIcon>
                </BoxLink>
              </NextLink>
            </Flex>
            <div className={styles.HeaderProductLinksContainer}>
              <HeaderProductLink
                active={router.pathname.startsWith('/themes/docs')}
                href="/themes/docs"
              >
                Documentation
              </HeaderProductLink>
              <HeaderProductLink
                active={router.pathname.startsWith('/themes/blocks')}
                href="/themes/blocks"
              >
                Blocks
              </HeaderProductLink>
              <HeaderProductLink
                active={router.pathname.startsWith('/themes/catalog')}
                href="/themes/catalog"
              >
                Components
              </HeaderProductLink>
              <HeaderProductLink href="/playroom" target="_blank">
                Playroom
              </HeaderProductLink>
            </div>
            <Flex
              align="center"
              bottom="0"
              display={{ initial: 'none', md: 'flex' }}
              gap="5"
              position="absolute"
              pr="4"
              right="0"
              top="0"
            >
              {children}
              <ThemeToggle />
              {gitHubLink ? (
                <Tooltip content="View GitHub">
                  <Link
                    color="gray"
                    data-chromatic="ignore"
                    href="https://www.radix-ui.com/primitives"
                    size="2"
                    target="_blank"
                  >
                    <Text
                      className="-mb-1"
                      color="neutral"
                      size="1"
                    >{`v${scaleUIRadixPackageJson.version}`}</Text>
                  </Link>
                </Tooltip>
              ) : null}
            </Flex>
            <Flex
              align="center"
              bottom="0"
              display={{ md: 'none' }}
              gap="4"
              position="absolute"
              pr="4"
              right="0"
              top="0"
            >
              <div className={styles.HeaderThemeToggleContainer}>
                <ThemeToggle />
              </div>
              <Tooltip content="Navigation">
                <IconButton
                  className={styles.MobileMenuButton}
                  color="gray"
                  data-state={mobileMenu.open ? 'open' : 'closed'}
                  onClick={() => {
                    mobileMenu.setOpen((open) => !open);
                  }}
                  size="3"
                  variant="ghost"
                >
                  <Icon icon={faBars} label="Menu" size="1" />
                </IconButton>
              </Tooltip>
            </Flex>
          </div>
        </div>
      </div>
    </Theme>
  );
}

function HeaderProductLink({
  active,
  children,
  href = '',
  className,
  ...props
}: React.ComponentPropsWithoutRef<'a'> & { active?: boolean }) {
  return (
    <NextLink href={href} legacyBehavior passHref>
      <a
        className={`${styles.HeaderProductLink} ${className}`}
        data-state={active ? 'active' : 'inactive'}
        {...props}
      >
        <span className={styles.HeaderProductLinkInner}>{children}</span>
        <span className={styles.HeaderProductLinkInnerHidden}>{children}</span>
      </a>
    </NextLink>
  );
}

function RadixByWorkOSLogoLink() {
  return (
    <NextLink href="/" legacyBehavior passHref>
      <BoxLink>
        <Flex
          align="center"
          gap="2"
          style={{
            marginBottom: -6,
          }}
        >
          <AccessibleIcon label="Made by Scale">
            <Flex align="baseline" gap="1">
              <svg
                style={{
                  color: 'black',
                  width: 'auto',
                  height: 24,
                }}
                viewBox="0 0 489 157"
              >
                <path d="M352.083 156.702a7.581 7.581 0 01-7.582-7.582V7.582A7.581 7.581 0 01352.083 0a7.581 7.581 0 017.583 7.582V149.12a7.581 7.581 0 01-7.583 7.582zm129.674-19.861a7.581 7.581 0 00-.871-10.687 7.581 7.581 0 00-10.687.871c-7.835 9.223-19.563 14.513-32.182 14.513-20.904 0-37.911-17.007-37.911-37.911s17.007-37.91 37.911-37.91c16.986 0 31.177 12.204 34.257 28.306h-46.391a7.581 7.581 0 00-7.582 7.583 7.581 7.581 0 007.582 7.582h54.593a7.58 7.58 0 007.582-7.582v-1.012c0-27.595-22.448-50.044-50.044-50.044-29.267 0-53.077 23.81-53.077 53.077s23.814 53.075 53.08 53.075c17.076 0 33.019-7.24 43.74-19.861zm-162.53 12.279v-48.529c0-27.595-22.448-50.043-50.043-50.043-29.268 0-53.078 23.81-53.078 53.077s23.81 53.077 53.078 53.077a7.581 7.581 0 007.582-7.582 7.58 7.58 0 00-7.582-7.582c-20.904 0-37.911-17.007-37.911-37.911s17.007-37.91 37.911-37.91c19.231 0 34.879 15.645 34.879 34.879v48.526a7.58 7.58 0 007.582 7.582 7.585 7.585 0 007.582-7.584zM89.711 126.373c0-7.4-2.718-17.494-15.668-23.559-7.77-3.64-17.52-5.135-26.949-6.581-22.09-3.387-29.934-6.217-29.934-15.355 0-9.955 13.805-15.164 27.443-15.164 8.594 0 20.944 1.733 30.986 9.991a7.584 7.584 0 009.635-11.715c-13.51-11.112-29.547-13.445-40.619-13.445-29.274 0-42.607 15.722-42.607 30.329 0 7.45 2.729 17.611 15.728 23.72 7.813 3.671 17.605 5.171 27.074 6.624 21.954 3.367 29.749 6.161 29.749 15.151 0 9.071-10.829 15.164-26.942 15.164-16.496 0-29.068-6.689-35.092-10.678a7.584 7.584 0 00-10.508 2.138 7.582 7.582 0 002.138 10.508c12.854 8.513 28.289 13.201 43.457 13.201 29.089 0 42.108-15.234 42.108-30.329zm109.939 7.95a7.585 7.585 0 00-2.117-10.513 7.584 7.584 0 00-10.513 2.118c-6.494 9.774-17.362 15.607-29.068 15.607-20.904 0-37.911-17.006-37.911-37.91 0-20.904 17.007-37.91 37.911-37.91 11.708 0 22.574 5.833 29.068 15.607a7.582 7.582 0 0010.513 2.117 7.581 7.581 0 002.117-10.512c-9.312-14.014-24.902-22.38-41.698-22.38-29.267 0-53.077 23.81-53.077 53.078 0 29.267 23.81 53.077 53.077 53.077 16.798 0 32.386-8.366 41.698-22.379z" />
              </svg>
              <Text
                size="5"
                style={{
                  fontWeight: '500',
                }}
              >
                UI
              </Text>
            </Flex>
          </AccessibleIcon>
          <AccessibleIcon label="Radix Homepage">
            <Flex align="baseline" gap="2">
              <Text size="6" weight="medium">
                Radix
              </Text>
            </Flex>
          </AccessibleIcon>
        </Flex>
      </BoxLink>
    </NextLink>
  );
}
